/**
 * Credential redaction for outgoing analytics payloads.
 *
 * PostHog autocaptures the page URL onto every event — not just as
 * `$current_url`, but also as `$session_entry_url` (stamped on EVERY event in
 * the session, which is why the blast radius is ~6x larger than the visible
 * one) and as nested copies inside `$set`, `$set_once` and the three
 * `$web_vitals_*_event` objects.
 *
 * Bower puts single-use bearer credentials in URLs in two places:
 *
 *   - `/m/capture?token=<64 hex>` — the mobile magic link. Redeeming it mints a
 *     Firebase custom token, i.e. a full session.
 *   - Firebase email action links — `?mode=resetPassword&oobCode=<code>`, where
 *     the code can set a new password.
 *
 * `$pageview` fires on mount, BEFORE the redemption effect resolves, so the
 * credential is still live (unredeemed, inside its TTL) at capture time. On the
 * redeem-failure path it stays live and unused. Redacting inside `before_send`
 * is therefore the only layer that covers every event type; the
 * `history.replaceState` in `MagicLinkRedemption` is complementary
 * defence-in-depth, not a substitute.
 *
 * @compliance security.md — "NEVER pass secrets in URL query params" and
 * "NEVER log secrets, even partially". This is the third-party-egress
 * equivalent of that rule. See also rules/egress-policy.md: PostHog is an
 * `observability` destination and is NOT BAA-covered.
 */

/** Replacement left in place of a credential value. */
export const REDACTED_PLACEHOLDER = '[redacted]';

/**
 * Matches `<separator><credential-param>=<value>`, capturing everything up to
 * and including the `=` so the parameter NAME survives redaction (analytics can
 * still distinguish a magic-link landing from a plain page view).
 *
 * Written as one literal rather than composed from named parts so it stays
 * within `security/detect-non-literal-regexp` — the pieces are, in order:
 *
 * 1. `((?:[?&#]|%3F|%26|%23)` — the separator, literal OR percent-encoded. The
 *    encoded forms matter because Firebase action links nest a whole URL inside
 *    `continueUrl=`, where `?` and `&` arrive as `%3F` / `%26`.
 * 2. `(?:[\w-]*(?:token|secret|password|signature)|oobcode|invitation)` — the
 *    parameter name. The `[\w-]*` prefix covers the whole family (`token`,
 *    `access_token`, `refresh_token`, `id_token`, `customToken`,
 *    `client_secret`) instead of an exhaustive list that silently rots as new
 *    auth flows land. `oobcode` matches Firebase's `oobCode` via the `i` flag.
 *    `signature` carries the same prefix so BOTH signed-URL generations are
 *    covered: GCS v2 emits `Signature=` (what `storage.service.ts` produces
 *    today, since `getSignedUrl` defaults to v2) and v4/S3 emit
 *    `X-Goog-Signature=` / `X-Amz-Signature=`. Signed media URLs reach
 *    `maskCapturedNetworkRequestFn` as replay network entries, so a switch to
 *    v4 signing would otherwise silently stop being redacted.
 *    `invitation` is the workspace-invite deep-link credential
 *    (`utils/pendingInvitation.ts`) — it does NOT end in `token`, so the family
 *    prefix misses it and it must be named explicitly. It was found leaking
 *    (1,139 events on `$session_entry_url`) only because the sibling
 *    enumeration was run extensionally rather than by intuition.
 * 3. `(?:=|%3D))` — the `=`, literal or encoded. End of capture group 1.
 * 4. `(?:(?!%26|%23|%3F)[^&#\s])*` — the value, running to the next separator.
 *    The negative lookahead is what makes the encoded case terminate: without
 *    it, `%26` inside a nested URL would be swallowed into the value and every
 *    following parameter destroyed rather than preserved.
 *
 * Deliberately NOT matched: `apiKey`. Firebase's Web API key rides along in
 * every action link, but it is public by design — it already ships inside the
 * JS bundle. Redacting it would imply a secret where there is none.
 *
 * NOTE: this regex carries the `g` flag, so it must only ever be used with
 * `String.replace` — `RegExp.test` on a `g` regex is stateful via `lastIndex`
 * and would intermittently return false on repeat calls. `replace` resets
 * `lastIndex`, so reuse is safe.
 */
const CREDENTIAL_IN_URL =
  /((?:[?&#]|%3F|%26|%23)(?:[\w-]*(?:token|secret|password|signature)|oobcode|invitation)(?:=|%3D))(?:(?!%26|%23|%3F)[^&#\s])*/gi;

/**
 * Strips credential values out of a URL-ish string, preserving the parameter
 * name so analytics can still tell "this was a magic-link landing" apart from
 * "this was a plain page view".
 *
 * Operates on the raw string rather than via `new URL()` on purpose: the same
 * helper has to handle absolute URLs, relative paths, and bare fragments, and
 * `new URL()` throws on the latter two.
 */
export function redactUrlCredentials(value: string): string {
  return value
    .replace(CREDENTIAL_IN_URL, `$1${REDACTED_PLACEHOLDER}`)
    .replace(SHARE_TOKEN_IN_PATH, `/share/${REDACTED_PLACEHOLDER}`);
}

/**
 * A public share link carries its credential in the PATH (`/share/<token>`,
 * the same shape `toPathTemplate` in `@bower/shared` templates), so the query
 * rule above never sees it. Anyone holding the token can read the shared note.
 */
const SHARE_TOKEN_IN_PATH = /\/share\/[a-f0-9]{32,}/gi;

/**
 * Depth limit for the property walk. `$web_vitals_LCP_event.$current_url` and
 * `$set_once.$initial_current_url` are the deepest real cases (depth 2); 4
 * leaves headroom without risking a pathological walk on a cyclic object.
 */
const MAX_DEPTH = 4;

/**
 * Recursively redacts every string value in place.
 *
 * Walks ALL properties rather than an allowlist of known URL keys. That is the
 * deliberate choice: the leak was originally reported as `$current_url` only,
 * but auditing the live data turned up eight distinct property paths carrying
 * the token, three of them nested. An allowlist would have to be extended every
 * time PostHog adds a URL-bearing property — and would fail silently when it
 * isn't (rule 12).
 */
function redactStringsInPlace(target: Record<string, unknown>, depth: number): void {
  if (depth > MAX_DEPTH) return;

  for (const [key, value] of Object.entries(target)) {
    if (typeof value === 'string') {
      target[key] = redactUrlCredentials(value);
    } else if (value !== null && typeof value === 'object') {
      redactStringsInPlace(value as Record<string, unknown>, depth + 1);
    }
  }
}

/**
 * Redacts credentials from an outgoing PostHog event's properties, in place.
 *
 * Mutates rather than clones: `before_send` is documented to return a modified
 * event, every caller builds its property object fresh, and cloning on the hot
 * path of every captured event would be wasteful.
 *
 * Never throws — analytics must never break the app, and a redactor that threw
 * would fail OPEN (PostHog would send the unredacted event). Callers get the
 * event back regardless; the `catch` re-runs nothing, so a partially-redacted
 * event is still strictly better than an unredacted one.
 */
export function redactEventCredentials<T>(event: T): T {
  if (event === null || typeof event !== 'object') return event;
  // posthog-js puts `$set` / `$set_once` (with `$initial_current_url`) at the
  // TOP level of the event, beside `properties`, so walk all three.
  for (const key of ['properties', '$set', '$set_once'] as const) {
    const block = (event as Record<string, unknown>)[key];
    if (block === null || typeof block !== 'object') continue;
    try {
      redactStringsInPlace(block as Record<string, unknown>, 0);
    } catch {
      // Intentionally ignored — see above.
    }
  }

  return event;
}

/** Replacement left in place of an exception message. */
export const EXCEPTION_MESSAGE_PLACEHOLDER = '[message removed]';

/**
 * Replaces every message on an outgoing `$exception` event, keeping type,
 * mechanism and stack frames.
 *
 * An error message is the classic user-content carrier (a validation error
 * naming the note, a thrown object serialized whole), and PostHog is not
 * BAA-covered. Covers the hand-written `captureException` calls too, because
 * every `$exception` passes through `before_send`.
 *
 * Fails CLOSED, unlike `redactEventCredentials`: an event it cannot clean is
 * dropped (`null`), never sent with its message.
 */
export function stripExceptionMessages<T>(event: T): T | null {
  try {
    const properties = (event as { properties?: Record<string, unknown> } | null)?.properties;
    if (!properties) return event;
    const list = properties['$exception_list'];
    if (Array.isArray(list)) {
      for (const entry of list) {
        if (entry !== null && typeof entry === 'object' && 'value' in entry) {
          (entry as { value: unknown }).value = EXCEPTION_MESSAGE_PLACEHOLDER;
        }
      }
    }
    if ('$exception_message' in properties) {
      properties['$exception_message'] = EXCEPTION_MESSAGE_PLACEHOLDER;
    }
    return event;
  } catch {
    return null;
  }
}

/**
 * Drops the query string and fragment from a URL a replay captured as a
 * network or performance entry. A query can carry a search term or other user
 * text as well as a credential, and the path is all a replay needs. Never
 * throws: a non-URL string loses everything from the first `?` or `#`.
 */
export function stripUrlQuery(value: string): string {
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
}

/**
 * Super properties Bower registered until 2026-10-13. posthog-js persists
 * super properties in the browser, so a returning browser keeps attaching
 * them to every event long after the code stopped registering them. A
 * workspace name is user content; the email and display name are personal
 * data that belong on the person, not on every behavioral event.
 */
const RETIRED_SUPER_PROPERTIES = ['workspace_name', 'user_email', 'user_display_name'] as const;

/** Removes the retired super properties from an outgoing event, in place. */
export function dropRetiredSuperProperties<T>(event: T): T {
  const properties = (event as { properties?: Record<string, unknown> } | null)?.properties;
  if (properties && typeof properties === 'object') {
    for (const key of RETIRED_SUPER_PROPERTIES) delete properties[key];
  }
  return event;
}

/** `href="..."` / `attr__href="..."` segments inside a `$elements_chain` string. */
const HREF_IN_ELEMENTS_CHAIN = /(?:attr__)?href="(?:[^"\\]|\\.)*"/g;

/**
 * Removes link targets from an autocaptured click. `mask_all_element_attributes`
 * does not cover them: posthog-js adds the clicked link's `href` to the element
 * chain and `$external_click_url` itself, and a link in a note is a URL the
 * user wrote. Which element was clicked survives; where it pointed does not.
 */
export function dropLinkTargets<T>(event: T): T {
  const properties = (event as { properties?: Record<string, unknown> } | null)?.properties;
  if (!properties || typeof properties !== 'object') return event;
  delete properties['$external_click_url'];
  const chain = properties['$elements_chain'];
  if (typeof chain === 'string') {
    properties['$elements_chain'] = chain.replace(HREF_IN_ELEMENTS_CHAIN, '');
  }
  const elements = properties['$elements'];
  if (Array.isArray(elements)) {
    for (const element of elements) {
      if (element !== null && typeof element === 'object') {
        delete (element as Record<string, unknown>)['href'];
        delete (element as Record<string, unknown>)['attr__href'];
      }
    }
  }
  return event;
}

/** Set once a browser has dropped the pre-2026-10-13 stored properties. */
const RETIRED_PROPS_CLEARED_KEY = 'bower_posthog_retired_props_cleared_v1';

interface FlagPropertyStore {
  unregister(property: string): void;
  resetPersonPropertiesForFlags(): void;
  resetGroupPropertiesForFlags(groupType?: string): void;
}

/**
 * One-time cleanup for a returning browser. `before_send` strips the retired
 * super properties from events, but posthog-js also keeps them, and the
 * workspace name it stored as person and group properties, in persistence,
 * and sends those on every `/flags` request, which `before_send` never sees.
 * Identity re-sets the person properties it still needs (email, role) on the
 * next `identifyUser`. Runs once per browser, only under persisted (consented)
 * storage; never throws, because analytics must not break app start.
 */
export function forgetRetiredFlagProperties(store: FlagPropertyStore): void {
  try {
    if (localStorage.getItem(RETIRED_PROPS_CLEARED_KEY)) return;
    for (const key of RETIRED_SUPER_PROPERTIES) store.unregister(key);
    store.resetPersonPropertiesForFlags();
    store.resetGroupPropertiesForFlags('workspace');
    localStorage.setItem(RETIRED_PROPS_CLEARED_KEY, new Date().toISOString());
  } catch {
    // Storage blocked or an SDK without these methods: try again next load.
  }
}
